export function parseJsonIfPossible(value: unknown): unknown {
  if (typeof value !== "string") return value
  const normalized = value.trim()
  if (!normalized) return value

  try {
    return JSON.parse(normalized)
  } catch {
    return value
  }
}

/** What the AI_ROLE_RESOLUTION step records, once parsed out of the raw step. */
export type RoleResolutionSummary = {
  resolvedRole: string
  decision: string
  settledOnThisTurn: boolean
  eligibleRoles: string[]
  classifiedRole: string | null
  contextMode: string | null
  capabilities: { rag: boolean; action: boolean; commerce: boolean }
  reason?: string | null
}

type RoleStepLike = {
  step?: unknown
  reason?: string | null
  request?: unknown
  response?: unknown
}

export const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

const asRoleList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : []

const asStringOrNull = (value: unknown): string | null =>
  typeof value === "string" && value ? value : null

/**
 * Pulls the run's role decision out of the step list. Returns null for a run recorded before the
 * step existed, so every caller has to keep working without it.
 */
export function getRoleResolution(
  steps: RoleStepLike[] | undefined | null
): RoleResolutionSummary | null {
  const step = (steps ?? []).find(
    (candidate) => String(candidate.step) === "AI_ROLE_RESOLUTION"
  )
  if (!step) return null

  const response = asRecord(step.response)
  const request = asRecord(step.request)
  const resolvedRole = asStringOrNull(response?.resolvedRole)
  if (!resolvedRole) return null

  const capabilities = asRecord(response?.capabilities)

  return {
    resolvedRole,
    decision: asStringOrNull(response?.decision) ?? "",
    settledOnThisTurn: response?.settledOnThisTurn === true,
    eligibleRoles: asRoleList(request?.eligibleRoles),
    classifiedRole: asStringOrNull(request?.classifiedRole),
    contextMode: asStringOrNull(request?.contextMode),
    capabilities: {
      rag: capabilities?.rag === true,
      action: capabilities?.action === true,
      commerce: capabilities?.commerce === true
    },
    reason: step.reason ?? null
  }
}

/** Turkish copy for the internal audit surface — see the hardcoded-Turkish exception in CLAUDE.md. */
export const ROLE_LABELS: Record<string, string> = {
  SERVICE: "Service",
  ECOMMERCE: "E-commerce",
  MARKETPLACE: "Marketplace"
}

export const ROLE_DECISION_LABELS: Record<string, string> = {
  SINGLE_ELIGIBLE_ROLE: "Tek uygun rol",
  ENRICH_CLASSIFIED: "Enrich sınıflandırdı",
  ENRICH_UNSURE_FALLBACK: "Enrich emin değil — varsayılan",
  ALREADY_SETTLED: "Zaten bu role oturmuş",
  SWITCHED_ON_NEW_TOPIC: "Yeni konu — rol değişti",
  KEPT_SETTLED_MID_THREAD: "Konu sürüyor — rol korundu",
  KEPT_SETTLED_NO_CAPABILITY_GAIN: "Yeni konu ama yetenek kaybı — rol korundu"
}

/** The three payloads a step accordion renders, and the ones data search looks inside. */
export type StepSection = "req" | "res" | "src"

export const STEP_SECTIONS: StepSection[] = ["req", "res", "src"]

type StepPayloadLike = {
  request?: unknown
  response?: unknown
  source?: unknown
}

export function stepSectionPayload(
  step: StepPayloadLike,
  section: StepSection
): unknown {
  const payload: unknown =
    section === "req"
      ? step.request
      : section === "res"
        ? step.response
        : step.source
  return payload
}

export function serializeForSearch(payload: unknown): string {
  if (payload === null || payload === undefined) return ""
  if (typeof payload === "string") return payload
  try {
    return JSON.stringify(payload) ?? ""
  } catch {
    return ""
  }
}

/**
 * Serializing a payload is the expensive half of a data search, so the caller keeps a cache that
 * lives as long as the step list does and every section is stringified at most once.
 */
export function cachedSearchText(
  cache: Map<string, string>,
  key: string,
  payload: unknown
): string {
  const cached = cache.get(key)
  if (cached !== undefined) return cached

  const text = serializeForSearch(payload).toLocaleLowerCase("tr")
  cache.set(key, text)
  return text
}

export function sectionSearchText(
  cache: Map<string, string>,
  stepId: string,
  step: StepPayloadLike,
  section: StepSection
): string {
  return cachedSearchText(
    cache,
    `${stepId}:${section}`,
    stepSectionPayload(step, section)
  )
}

/**
 * Turkish lowercasing can change a string's length, which would shift the offsets, so such a
 * string reports no match. The renderer and the counter share this so "3 / 27" lands on a mark.
 */
export function findMatchOffsets(text: string, needle: string): number[] {
  const target = needle.toLocaleLowerCase("tr")
  if (!target || target.length !== needle.length) return []

  const haystack = text.toLocaleLowerCase("tr")
  if (haystack.length !== text.length) return []

  const offsets: number[] = []
  let at = haystack.indexOf(target)
  while (at !== -1) {
    offsets.push(at)
    at = haystack.indexOf(target, at + target.length)
  }
  return offsets
}

/** Mirrors what the JSON viewer marks: keys, strings and numbers — never booleans or null. */
export function countRenderedMatches(value: unknown, needle: string): number {
  if (typeof value === "string") return findMatchOffsets(value, needle).length
  if (typeof value === "number") {
    return findMatchOffsets(String(value), needle).length
  }
  if (Array.isArray(value)) {
    return value.reduce<number>(
      (sum, item) => sum + countRenderedMatches(item, needle),
      0
    )
  }
  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).reduce<number>(
      (sum, [key, item]) =>
        sum +
        findMatchOffsets(key, needle).length +
        countRenderedMatches(item, needle),
      0
    )
  }
  return 0
}

export type DataSearchScope = "all" | "incoming"

export type DataKind =
  | "guidance"
  | "knowledge"
  | "store"
  | "marketplace"
  | "action"
  | "skill"

export const DATA_KINDS: DataKind[] = [
  "guidance",
  "knowledge",
  "store",
  "marketplace",
  "action",
  "skill"
]

export const DATA_KIND_LABELS: Record<DataKind, string> = {
  guidance: "Guidance",
  knowledge: "Bilgi kaynağı",
  store: "Mağaza",
  marketplace: "Pazaryeri",
  action: "Action / HTTP",
  skill: "Skill"
}

/**
 * A subtree of a step payload that came from outside the model. `path` is written the way the JSON
 * viewer names its nodes (`hits.hits`, `results`); `""` is the whole section.
 */
export type DataRegion = {
  section: StepSection
  path: string
  kind: DataKind
  value: unknown
}

type RegionRule = Omit<DataRegion, "value">

const STEP_DATA_RULES: Record<string, RegionRule[]> = {
  GUIDANCE_SELECTION: [{ section: "res", path: "", kind: "guidance" }],
  AGENT_CONTEXT: [
    { section: "req", path: "guidance.rows", kind: "guidance" },
    { section: "req", path: "guidance.droppedRows", kind: "guidance" }
  ],
  GET_PINECONE_DATA: [{ section: "res", path: "matches", kind: "knowledge" }],
  GET_ELASTIC_BM25_DATA: [
    { section: "res", path: "hits.hits", kind: "knowledge" }
  ],
  FUSE_RAG_RESULTS: [{ section: "res", path: "results", kind: "knowledge" }],
  CHECK_IMAGE_PINECONE: [{ section: "res", path: "matches", kind: "store" }],
  COMMERCE_MCP_TOOL_CALL: [{ section: "res", path: "", kind: "store" }],
  GET_QPIEN_SHOP_PRODUCT_DETAILS: [{ section: "res", path: "", kind: "store" }],
  MARKETPLACE_REQUEST: [{ section: "res", path: "data", kind: "marketplace" }],
  HTTP_REQUEST: [{ section: "res", path: "body", kind: "action" }],
  SKILL_AGENT: [
    { section: "res", path: "skillContext.instructions", kind: "skill" }
  ]
}

/** The v1 RAG answer steps carry the chunks they were given in `source.source`. */
const RAG_ANSWER_RULES: RegionRule[] = [
  { section: "src", path: "source", kind: "knowledge" }
]

/** A v2 tool result wraps its data in these; they echo the query or describe the call, not data. */
const TOOL_ENVELOPE_KEYS = new Set([
  "ok",
  "status",
  "query",
  "error",
  "detail",
  "appliedFilters",
  "diagnostics"
])

function agentToolKind(tool: string): DataKind {
  if (tool === "search_knowledge" || tool === "skill_knowledge") {
    return "knowledge"
  }
  if (
    tool.startsWith("action_") ||
    tool === "skill_action" ||
    tool === "skill_write_action"
  ) {
    return "action"
  }
  if (tool.startsWith("skill_") || tool === "start_skill") return "skill"
  return "store"
}

function valueAtPath(root: unknown, path: string): unknown {
  if (!path) return root
  return path.split(".").reduce<unknown>((node, key) => {
    const record = asRecord(node)
    return record ? record[key] : undefined
  }, root)
}

function agentToolRegions(step: StepPayloadLike): DataRegion[] {
  const tool = asRecord(parseJsonIfPossible(step.request))?.tool
  const kind = agentToolKind(typeof tool === "string" ? tool : "")
  const response = parseJsonIfPossible(step.response)
  const record = asRecord(response)
  if (!record) {
    return response === null || response === undefined
      ? []
      : [{ section: "res", path: "", kind, value: response }]
  }
  return Object.entries(record)
    .filter(
      ([key, value]) =>
        !TOOL_ENVELOPE_KEYS.has(key) && value !== null && value !== undefined
    )
    .map(([key, value]) => ({ section: "res", path: key, kind, value }))
}

export function extractDataRegions(
  step: StepPayloadLike & { step?: unknown }
): DataRegion[] {
  const name = String(step.step ?? "")
  if (name === "AGENT_TOOL_CALL") return agentToolRegions(step)

  const rules = name.startsWith("ANSWER_BY_RAG_")
    ? RAG_ANSWER_RULES
    : STEP_DATA_RULES[name]
  if (!rules) return []

  return rules.flatMap((rule) => {
    const value = valueAtPath(
      parseJsonIfPossible(stepSectionPayload(step, rule.section)),
      rule.path
    )
    return value === null || value === undefined ? [] : [{ ...rule, value }]
  })
}

/** Keys at the region's own path name the field, not the data, so only what is below it counts. */
export function isInsideRegion(
  path: string,
  region: string,
  isKey: boolean
): boolean {
  if (region === "") return true
  if (path === region) return !isKey
  return path.startsWith(`${region}.`) || path.startsWith(`${region}[`)
}

/** Steps whose `durationMs` spans the steps nested inside them; the breakdown labels them apart. */
export const CONTAINER_DURATION_STEPS = new Set(["AGENT_OUTPUT"])

export function isContainerDurationStep(step: unknown): boolean {
  return CONTAINER_DURATION_STEPS.has(String(step))
}

type DurationStepLike = {
  step?: unknown
  durationMs?: number | null
  response?: unknown
}

export type TurnTimings = {
  prepareMs: number
  agentMs: number
  settleMs: number
  totalMs: number
}

const asMillis = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : null

/**
 * The turn's own clock, written by v2 on DELIVERY_GATE. It also covers the stretches no step
 * measures — context build, delivery — so it is the honest total wherever a run carries it.
 */
export function getTurnTimings(
  steps: DurationStepLike[] | undefined | null
): TurnTimings | null {
  const gate = (steps ?? []).find(
    (step) => String(step.step) === "DELIVERY_GATE"
  )
  if (!gate) return null

  const response = asRecord(parseJsonIfPossible(gate.response))
  const timings = asRecord(response?.timings)
  if (!timings) return null

  const prepareMs = asMillis(timings.prepareMs)
  const agentMs = asMillis(timings.agentMs)
  const settleMs = asMillis(timings.settleMs)
  if (prepareMs === null && agentMs === null && settleMs === null) return null

  return {
    prepareMs: prepareMs ?? 0,
    agentMs: agentMs ?? 0,
    settleMs: settleMs ?? 0,
    totalMs: (prepareMs ?? 0) + (agentMs ?? 0) + (settleMs ?? 0)
  }
}

export type RunTotals = {
  durationMs: number | null
  inputToken: number
  cachedInputToken: number
  outputToken: number
  amount: number
  llmCalls: number
}

/**
 * The run's totals exactly as qpien-ai stamped them when the run closed. Never derived from the
 * steps here: container steps repeat their children, so any sum drawn from the list double counts.
 * Null for a run still open or one recorded before the totals existed.
 */
export function getRunTotals(
  run:
    | {
        durationMs?: number | null
        usage?: {
          inputToken?: number | null
          cachedInputToken?: number | null
          outputToken?: number | null
          amount?: number | null
          llmCalls?: number | null
        } | null
      }
    | null
    | undefined
): RunTotals | null {
  const durationMs = asMillis(run?.durationMs)
  const usage = run?.usage
  if (durationMs === null && !usage) return null
  return {
    durationMs,
    inputToken: usage?.inputToken ?? 0,
    cachedInputToken: usage?.cachedInputToken ?? 0,
    outputToken: usage?.outputToken ?? 0,
    amount: usage?.amount ?? 0,
    llmCalls: usage?.llmCalls ?? 0
  }
}
